import React, { useState, useEffect, useRef, useCallback } from 'react';
import faceApiService from '../../services/biometric/faceApiService';
import './FaceBiometricModal.css';

/**
 * Enterprise LIMS Biometric Face Recognition Modal.
 * Unified modal supporting Employee Face Login, Employee Enrollment,
 * Patient Identification, and Patient Enrollment with live face detection,
 * liveness verification, and automatic MediaStream track cleanup.
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - mode: 'login' | 'enroll_employee' | 'identify_patient' | 'enroll_patient'
 * - title: string (optional)
 * - employeeData: { employeeId, name, username } (for employee enrollment)
 * - patientUhid: string (for patient enrollment)
 * - onSuccess: (result) => void
 * - onFallback: () => void (for password/manual search fallback)
 */
export const FaceBiometricModal = ({
  isOpen,
  onClose,
  mode = 'login',
  title = null,
  employeeData = null,
  patientUhid = null,
  onSuccess,
  onFallback,
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isProcessingRef = useRef(false);
  const samplesRef = useRef([]);

  // Throttled UI State Refs to prevent excessive React re-renders in 60fps loop
  const lastGuidanceRef = useRef('');
  const lastQualityRef = useRef(0);
  const lastLivenessRef = useRef('');
  const lastUiUpdateRef = useRef(0);
  const timingRef = useRef({ openTime: 0, cameraReadyTime: 0, modelReadyTime: 0 });

  // UI States
  const [isLoadingCamera, setIsLoadingCamera] = useState(true);
  const [isLoadingModels, setIsLoadingModels] = useState(!faceApiService.isModelsLoaded());
  const [cameraError, setCameraError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [guidance, setGuidance] = useState('Position your face inside the framing guide');
  const [livenessStatus, setLivenessStatus] = useState('Verifying presence...');
  const [qualityScore, setQualityScore] = useState(0);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [progress, setProgress] = useState(0);

  // Derive dynamic dialog title
  const modalTitle = title || (
    mode === 'login' ? 'Staff Facial Recognition Login' :
    mode === 'enroll_employee' ? `Enroll Face Biometrics: ${employeeData?.name || employeeData?.username || 'Staff'}` :
    mode === 'identify_patient' ? 'Patient Biometric Identification' :
    'Enroll Patient Facial Biometrics'
  );

  // Stop camera media tracks safely
  const stopCamera = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.error('Error stopping stream tracks:', err);
      }
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Discover available camera devices asynchronously
  const discoverDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === 'videoinput'));
    } catch {
      // non-blocking
    }
  }, []);

  // Start webcam with fast constraints and non-blocking device enumeration
  const startCamera = useCallback(async (deviceId = null) => {
    setIsLoadingCamera(true);
    setCameraError(null);
    setErrorMessage(null);
    samplesRef.current = [];
    setProgress(0);

    try {
      const constraints = {
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : {
              width: { ideal: 640 },
              height: { ideal: 480 },
              facingMode: 'user',
              frameRate: { ideal: 30, max: 30 },
            },
        audio: false,
      };

      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        // Fallback to basic constraints
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }

      timingRef.current.cameraReadyTime = performance.now();
      setIsLoadingCamera(false);

      // Enumerate available hardware asynchronously without delaying viewfinder readiness
      discoverDevices();
    } catch (err) {
      setIsLoadingCamera(false);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError({
          type: 'denied',
          title: 'Camera Access Denied',
          message: 'Camera permission was refused. Please enable camera access in your browser or use password login.',
        });
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraError({
          type: 'not_found',
          title: 'No Webcam Detected',
          message: 'No video capture hardware was detected on this workstation.',
        });
      } else {
        setCameraError({
          type: 'error',
          title: 'Camera Initialization Failed',
          message: 'Unable to connect to camera. Please verify device connection.',
        });
      }
    }
  }, [discoverDevices]);

  // Load models & camera in parallel on mount
  useEffect(() => {
    if (!isOpen) return;

    timingRef.current.openTime = performance.now();
    let mounted = true;

    if (faceApiService.isModelsLoaded()) {
      setIsLoadingModels(false);
      timingRef.current.modelReadyTime = performance.now();
    } else {
      setIsLoadingModels(true);
      faceApiService
        .loadModels()
        .then(() => {
          if (mounted) {
            timingRef.current.modelReadyTime = performance.now();
            setIsLoadingModels(false);
          }
        })
        .catch((err) => {
          if (mounted) {
            setIsLoadingModels(false);
            setErrorMessage(err.message || 'Failed to load biometric models.');
          }
        });
    }

    startCamera();

    return () => {
      mounted = false;
      stopCamera();
    };
  }, [isOpen, startCamera, stopCamera]);

  // Real-time Detection Loop with High-Throughput Throttled UI Updates
  useEffect(() => {
    if (!isOpen || isLoadingCamera || isLoadingModels || cameraError || isAuthenticating) {
      return;
    }

    let isRunning = true;
    const isIdentification = mode === 'identify_patient' || mode === 'login';
    // Adaptive sample threshold: 1 frame for pristine quality (>=0.80), 2 for good (>=0.62), 3 for enrollment
    const requiredSamplesForQuality = (q) => {
      if (!isIdentification) return 3;
      if (q >= 0.80) return 1;
      return 2;
    };

    const detectLoop = async () => {
      if (!isRunning) return;

      const video = videoRef.current;
      if (video && video.readyState >= 2 && !isProcessingRef.current) {
        isProcessingRef.current = true;

        try {
          const tFrameStart = performance.now();
          const res = await faceApiService.detectFaceAndDescriptor(video);
          const tInference = Math.round(performance.now() - tFrameStart);

          const now = performance.now();
          const shouldUpdateUi = now - lastUiUpdateRef.current >= 120; // 120ms throttle to protect 60fps render loop

          if (!res) {
            if (shouldUpdateUi || lastGuidanceRef.current !== 'Align your face inside the framing oval') {
              lastGuidanceRef.current = 'Align your face inside the framing oval';
              setGuidance('Align your face inside the framing oval');
              setLivenessStatus('Searching for subject...');
              setQualityScore(0);
              lastUiUpdateRef.current = now;
            }
          } else {
            // Real-time EAR liveness metric
            const ear = faceApiService.calculateEyeAspectRatio(res.landmarks);
            const liveText = ear < 0.18 ? 'Natural eye blink detected ✓' : 'Subject verified active ✓';

            if (shouldUpdateUi || lastGuidanceRef.current !== res.guideMessage) {
              lastGuidanceRef.current = res.guideMessage;
              setGuidance(res.guideMessage);
              setQualityScore(res.quality);
              setLivenessStatus(liveText);
              lastUiUpdateRef.current = now;
            }

            // If quality is acceptable (>= 0.60) and properly framed, accumulate sample
            if (res.quality >= 0.60 && res.guideMessage.includes('Hold steady')) {
              samplesRef.current.push(res.descriptor);
              const count = samplesRef.current.length;
              const targetCount = requiredSamplesForQuality(res.quality);

              setProgress(Math.min(100, Math.round((count / targetCount) * 100)));

              // Once target sample count is reached, dispatch immediately
              if (count >= targetCount) {
                isRunning = false;
                console.info(`[Biometric Pipeline] Frame inference: ${tInference}ms, Samples: ${count}, Quality: ${res.quality}`);
                handleSamplesReady(samplesRef.current, res.quality);
                return;
              }
            } else {
              // Reset if subject leaves or moves significantly out of frame
              if (samplesRef.current.length > 0 && res.quality < 0.45) {
                samplesRef.current = [];
                setProgress(0);
              }
            }
          }
        } catch (scanErr) {
          console.warn('[FaceBiometricModal] Scan frame error:', scanErr);
        } finally {
          isProcessingRef.current = false;
        }
      }

      if (isRunning) {
        animationFrameRef.current = requestAnimationFrame(detectLoop);
      }
    };

    animationFrameRef.current = requestAnimationFrame(detectLoop);

    return () => {
      isRunning = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isOpen, isLoadingCamera, isLoadingModels, cameraError, isAuthenticating, mode]);

  // Average collected descriptor vectors
  const averageVectors = (vectorList) => {
    if (!vectorList || vectorList.length === 0) return [];
    if (vectorList.length === 1) return vectorList[0];

    const len = vectorList[0].length;
    const avg = new Array(len).fill(0);
    for (let i = 0; i < len; i++) {
      let sum = 0;
      for (let j = 0; j < vectorList.length; j++) {
        sum += vectorList[j][i];
      }
      avg[i] = sum / vectorList.length;
    }
    return avg;
  };

  // Dispatch gathered descriptor to parent
  const handleSamplesReady = async (samples, quality) => {
    setIsAuthenticating(true);
    setGuidance('Facial features captured. Processing...');
    const finalizedVector = averageVectors(samples);

    try {
      await onSuccess?.({
        faceEncoding: finalizedVector,
        quality,
      });
      stopCamera();
    } catch (err) {
      console.error('[FaceBiometricModal] Submission error:', err);
      const msg = err.response?.data?.message || err.message || 'Face verification failed.';
      setErrorMessage(msg);
      setIsAuthenticating(false);
      samplesRef.current = [];
      setProgress(0);
    }
  };

  // Close safely
  const handleClose = () => {
    stopCamera();
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="face-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="face-modal-title">
      <div className="face-modal-card">
        {/* Header */}
        <div className="face-modal-header">
          <div className="face-modal-title-wrap">
            <span className="face-modal-badge">
              <span className="face-pulse-dot"></span> BIOMETRIC
            </span>
            <h2 id="face-modal-title">{modalTitle}</h2>
          </div>
          <button
            type="button"
            className="face-modal-close-btn"
            onClick={handleClose}
            aria-label="Close biometric modal"
          >
            ✕
          </button>
        </div>

        {/* Viewfinder Body */}
        <div className="face-modal-body">
          {cameraError ? (
            <div className="face-camera-error-pane">
              <div className="face-error-icon">⚠️</div>
              <h3>{cameraError.title}</h3>
              <p>{cameraError.message}</p>
              <div className="face-error-actions">
                <button
                  type="button"
                  className="face-btn-primary"
                  onClick={() => startCamera(selectedDeviceId)}
                >
                  Retry Camera
                </button>
                {onFallback && (
                  <button
                    type="button"
                    className="face-btn-secondary"
                    onClick={() => {
                      handleClose();
                      onFallback();
                    }}
                  >
                    {mode === 'login' ? 'Use Password Login' : 'Use Manual Search'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="face-viewfinder-container">
              {/* Live WebCam Stream */}
              <video
                ref={videoRef}
                className="face-video-feed"
                playsInline
                muted
                autoPlay
              />

              {/* Oval Framing Guide Overlay */}
              <div className={`face-oval-frame ${qualityScore >= 0.60 ? 'frame-matched' : ''}`}>
                <div className="face-scan-line"></div>
              </div>

              {/* Progress Indicator */}
              {progress > 0 && (
                <div className="face-progress-bar-wrap">
                  <div className="face-progress-bar-fill" style={{ width: `${progress}%` }}></div>
                </div>
              )}

              {/* Status / Loading Overlay */}
              {(isLoadingCamera || isLoadingModels || isAuthenticating) && (
                <div className="face-stream-loading">
                  <div className="face-spinner"></div>
                  <span>
                    {isLoadingCamera ? 'Connecting to camera...' :
                     isLoadingModels ? 'Loading neural networks...' :
                     'Verifying face credentials...'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Feedback & Guidance Strip */}
          {!cameraError && (
            <div className="face-feedback-bar">
              <div className="face-guidance-text">
                <span className="face-info-icon">ℹ</span> {guidance}
              </div>
              <div className="face-metrics-row">
                <span className="face-metric-item">
                  <strong>Liveness:</strong> {livenessStatus}
                </span>
                <span className="face-metric-item">
                  <strong>Quality:</strong> {Math.round(qualityScore * 100)}%
                </span>
              </div>
            </div>
          )}

          {/* Error Banner */}
          {errorMessage && (
            <div className="face-error-banner" role="alert">
              <span>⚠️ {errorMessage}</span>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="face-modal-footer">
          {devices.length > 1 && (
            <div className="face-device-picker">
              <label htmlFor="face-camera-select">Camera:</label>
              <select
                id="face-camera-select"
                value={selectedDeviceId}
                onChange={(e) => {
                  setSelectedDeviceId(e.target.value);
                  startCamera(e.target.value);
                }}
              >
                {devices.map((d, idx) => (
                  <option key={d.deviceId || idx} value={d.deviceId}>
                    {d.label || `Camera ${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="face-footer-buttons">
            {onFallback && (
              <button
                type="button"
                className="face-btn-secondary"
                onClick={() => {
                  handleClose();
                  onFallback();
                }}
              >
                {mode === 'login' ? 'Password Login' : 'Manual Search'}
              </button>
            )}
            <button
              type="button"
              className="face-btn-cancel"
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaceBiometricModal;
